import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import PageHeader from '../../components/common/PageHeader';
import TableSkeleton from '../../components/common/TableSkeleton';
import { sandboxService } from '../../services/sandboxService';
import { SandboxFindingListItem } from '../../types';
import { formatSeverityLower, getKritikalitaetChipSx } from '../../utils/severity';
import { format } from 'date-fns';
import { parseUtcDateTime } from '../../utils/time';

/** Startseite der eingeschraenkten Sitzung: eigene Sandbox-Findings. */
const SandboxFindingsListPage: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<SandboxFindingListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [includeArchived, setIncludeArchived] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await sandboxService.list(includeArchived));
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [includeArchived]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Box>
      <PageHeader
        title="Meine Findings"
        subtitle="Isolierte Umgebung – diese Findings gehören noch zu keinem Audit"
        actions={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" startIcon={<MenuBookIcon />} onClick={() => navigate('/sandbox/api-docs')}>
              API-Dokumentation
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/sandbox/findings/neu')} data-testid="sandbox-new">
              Neues Finding
            </Button>
          </Box>
        }
      />

      <Alert severity="info" sx={{ mb: 2 }}>
        Sie sind mit dem eingeschränkten Zugang angemeldet. Hier angelegte Findings werden später vom Hauptaccount
        geprüft und in ein Audit importiert. Findings können archiviert, aber nicht endgültig gelöscht werden.
      </Alert>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <FormControlLabel
          control={<Switch size="small" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} />}
          label={<Typography variant="body2">Archivierte anzeigen</Typography>}
        />
      </Box>

      {loading ? (
        <TableSkeleton />
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Titel</TableCell>
                <TableCell>Kritikalität</TableCell>
                <TableCell>CVSS</TableCell>
                <TableCell>Host</TableCell>
                <TableCell>Klasse</TableCell>
                <TableCell>Erstellt</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                      Noch keine Findings in der isolierten Umgebung.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {rows.map((r) => (
                <TableRow key={r.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/sandbox/findings/${r.id}`)}>
                  <TableCell sx={{ fontWeight: 600 }}>{r.titel}</TableCell>
                  <TableCell>
                    {r.kritikalitaet ? (
                      <Chip size="small" label={formatSeverityLower(r.kritikalitaet)} sx={getKritikalitaetChipSx(r.kritikalitaet)} />
                    ) : '–'}
                  </TableCell>
                  <TableCell>{r.cvss31_score != null ? r.cvss31_score.toFixed(1) : '–'}</TableCell>
                  <TableCell>{r.host || '–'}</TableCell>
                  <TableCell>{r.schwachstellenklasse?.name_de || '–'}</TableCell>
                  <TableCell>{r.erstellt_am ? format(parseUtcDateTime(r.erstellt_am), 'dd.MM.yyyy HH:mm') : '–'}</TableCell>
                  <TableCell>
                    {r.archiviert_am ? <Chip size="small" label="archiviert" color="warning" variant="outlined" /> : <Chip size="small" label="aktiv" color="success" variant="outlined" />}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default SandboxFindingsListPage;
